import { createSelector } from '@reduxjs/toolkit';

import type { BalanceAlertRule, BalanceAlertRulesState } from './types';

/**
 * Minimal state shape the balance-alert-rules selectors read from. Exported so the app can
 * check at compile time that its store mounts the slice here (see package-store-contracts).
 */
export interface BalanceAlertRulesStore {
  balanceAlertRules: BalanceAlertRulesState;
}

const NO_RULES: BalanceAlertRule[] = [];

/**
 * The rules of `projectId` as the user last left them: a flipped switch shows its new state
 * and a deleted rule is gone, ahead of the push that confirms it. The socket pushes the rules
 * of whichever project it has open, so after a switch the store keeps the previous project's
 * rules until the next push; those are hidden rather than shown under the wrong project.
 */
export const selectBalanceAlertRules = createSelector(
  [
    (store: BalanceAlertRulesStore) => store.balanceAlertRules,
    (_: BalanceAlertRulesStore, projectId: number | undefined) => projectId,
  ],
  ({ projectID, rules, pendingEnabled, deletedRules }, projectId): BalanceAlertRule[] => {
    if (projectId === undefined || projectID !== projectId) return NO_RULES;
    if (Object.keys(pendingEnabled).length === 0 && Object.keys(deletedRules).length === 0) {
      return rules;
    }
    return rules
      .filter((rule) => deletedRules[rule.id] === undefined)
      .map((rule) => {
        const pending = pendingEnabled[rule.id];
        return pending === undefined ? rule : { ...rule, enabled: pending.enabled };
      });
  },
);

/** Whether the first push for `projectId` has arrived (an empty list is still loaded). */
export const selectAreBalanceAlertRulesLoaded = (
  store: BalanceAlertRulesStore,
  projectId: number | undefined,
): boolean => projectId !== undefined && store.balanceAlertRules.projectID === projectId;

export const selectAddBalanceAlertRuleStatus = (store: BalanceAlertRulesStore) =>
  store.balanceAlertRules.status;
export const selectUpdateBalanceAlertRuleStatus = (store: BalanceAlertRulesStore) =>
  store.balanceAlertRules.updateStatus;
export const selectToggleBalanceAlertRuleStatus = (store: BalanceAlertRulesStore) =>
  store.balanceAlertRules.toggleStatus;
export const selectDeleteBalanceAlertRuleStatus = (store: BalanceAlertRulesStore) =>
  store.balanceAlertRules.deleteStatus;

export const selectAddBalanceAlertRuleError = (store: BalanceAlertRulesStore) =>
  store.balanceAlertRules.error;
export const selectUpdateBalanceAlertRuleError = (store: BalanceAlertRulesStore) =>
  store.balanceAlertRules.updateError;
export const selectToggleBalanceAlertRuleError = (store: BalanceAlertRulesStore) =>
  store.balanceAlertRules.toggleError;
export const selectDeleteBalanceAlertRuleError = (store: BalanceAlertRulesStore) =>
  store.balanceAlertRules.deleteError;
